// Kolonie na Keplerovi: metadata sdílená serverem, klientem i boty. Žádná logika.
// Mechaniky podle oficiálních pravidel základní hry (viz docs/DESIGN_stavitele.md), vlastní téma a texty.

export type Res = "kov" | "sklo" | "voda" | "jidlo" | "energie";
export const RESOURCES: Res[] = ["kov", "sklo", "voda", "jidlo", "energie"];
export type Terrain = Res | "krater";

export type Bag = Partial<Record<Res, number>>;

/** Ceny staveb (role v pravidlech: cesta, osada, město, rozvojová karta). */
export const COSTS = {
  tunel: { sklo: 1, kov: 1 },
  zakladna: { sklo: 1, kov: 1, voda: 1, jidlo: 1 },
  mesto: { energie: 3, jidlo: 2 },
  vyzkum: { energie: 1, voda: 1, jidlo: 1 },
} satisfies Record<string, Bag>;

export const LIMITS = { tunel: 15, zakladna: 5, mesto: 4 };

/** Kolik karet od každé suroviny má banka (oficiálně 19). */
export const BANK_PER_RES = 19;

/** Terény na 19 polích. */
export const TERRAIN_COUNTS: Record<Terrain, number> = { kov: 4, sklo: 3, voda: 4, jidlo: 4, energie: 3, krater: 1 };

/** 18 čísel; 6 a 8 nesmí ležet vedle sebe. */
export const TOKENS = [2, 3, 3, 4, 4, 5, 5, 6, 6, 8, 8, 9, 9, 10, 10, 11, 11, 12];

/** Typy 9 obchodních stanic: 4 obecné 3:1 a jedna 2:1 pro každou surovinu. */
export const PORT_TYPES: (Res | "any")[] = ["any", "any", "any", "any", ...RESOURCES];

/** Karty výzkumu (role: rytíř, stavba 2 cest, vezmi 2 suroviny, monopol, vítězný bod). */
export type Dev = "hlidka" | "tunely" | "zasoby" | "monopol" | "bod";
export const DEV_COUNTS: Record<Dev, number> = { hlidka: 14, tunely: 2, zasoby: 2, monopol: 2, bod: 5 };

/** Kolik „teček“ (šancí z 36) má číslo. */
export function pips(n: number | null): number {
  if (!n || n === 7) return 0;
  return 6 - Math.abs(7 - n);
}

export type Phase = "setup" | "roll" | "discard" | "robber" | "steal" | "main" | "roadBuilding" | "over";

export interface Building {
  owner: number;
  city: boolean;
}

export interface TradeOffer {
  id: number;
  /** Index hráče, který nabízí (vždy hráč na tahu). */
  from: number;
  give: Bag;
  want: Bag;
  /** null = všem. */
  to: number | null;
  /** Odpovědi ostatních: přijetí, odmítnutí nebo protinabídka (z pohledu nabízejícího: co dá / co chce). */
  responses: Record<number, { kind: "accept" } | { kind: "decline" } | { kind: "counter"; give: Bag; want: Bag }>;
}

/** Kdo teď musí nebo smí jednat a co. */
export interface Pending {
  player: string;
  kind: "setupSettlement" | "setupRoad" | "roll" | "discard" | "robber" | "steal" | "main" | "roadBuilding" | "tradeResponse";
  count?: number;
  offerId?: number;
}

export interface PublicPlayer {
  id: string;
  name: string;
  isBot: boolean;
  cards: number;
  devCards: number;
  knights: number;
  /** Body, které vidí všichni (bez skrytých bodových karet). */
  publicPoints: number;
  longestRoad: number;
  pieces: { tunel: number; zakladna: number; mesto: number };
}

export interface KolonieView {
  version: number;
  hexes: { terrain: Terrain; number: number | null }[];
  ports: { edge: number; type: Res | "any" }[];
  robber: number;
  corners: (Building | null)[];
  edges: (number | null)[];
  players: PublicPlayer[];
  /** Index hráče na tahu. */
  turn: number;
  phase: Phase;
  dice: [number, number] | null;
  bank: Record<Res, number>;
  devDeck: number;
  longestHolder: number | null;
  largestHolder: number | null;
  trade: TradeOffer | null;
  pending: Pending[];
  targetPoints: number;
  winner: number | null;
  /** Index „mě“ (null = divák) a moje skryté věci. */
  me: number | null;
  hand: Record<Res, number> | null;
  devHand: { kind: Dev; fresh: boolean }[];
  myPoints: number | null;
  discardNeeded: number;
  stealFrom: number[];
  freeRoads: number;
  devPlayedThisTurn: boolean;
  turnCount: number;
}

export type KolonieAction =
  | { type: "placeSettlement"; corner: number }
  | { type: "placeRoad"; edge: number }
  | { type: "roll" }
  | { type: "discard"; cards: Bag }
  | { type: "moveRobber"; hex: number }
  | { type: "steal"; victim: number }
  | { type: "buildRoad"; edge: number }
  | { type: "buildSettlement"; corner: number }
  | { type: "buildCity"; corner: number }
  | { type: "buyDev" }
  | { type: "playKnight" }
  | { type: "playRoadBuilding" }
  | { type: "playYearOfPlenty"; res: [Res, Res] }
  | { type: "playMonopoly"; res: Res }
  | { type: "bankTrade"; give: Res; get: Res }
  | { type: "offerTrade"; give: Bag; want: Bag; to: number | null }
  | { type: "respondTrade"; offerId: number; response: "accept" | "decline" }
  | { type: "counterTrade"; offerId: number; give: Bag; want: Bag }
  | { type: "confirmTrade"; offerId: number; with: number }
  | { type: "cancelTrade" }
  | { type: "endTurn" };
